// Web incoming-ringtone picker (Calls settings). The web app synthesizes its
// ring (no system ringtone concept), so unlike the native RingtoneSection this
// is an in-app catalog: tap a tone to preview it and select it. Selection is a
// device-local preference (storage/ringtonePref); IncomingCallScreen reads it
// when a call rings. Native uses the system picker instead, so this renders
// only on the web.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { RINGTONES, createRingtoneFor } from '@merkury/brain/call/ringtones.js';
import type { TonePlayer } from '@merkury/brain/call/reconnectTone.js';
import { isCapacitorNative } from '../../shell/capacitor/platform.js';
import { getRingtoneId, setRingtoneId, subscribeRingtone } from '../../storage/ringtonePref.js';
import { useT } from '../../i18n/index.js';
import { Section } from './chrome.js';

function useRingtoneId(): string {
  const [id, setId] = useState(getRingtoneId);
  useEffect(() => subscribeRingtone(() => setId(getRingtoneId())), []);
  return id;
}

function RingtonePicker(): ReactNode {
  const t = useT();
  const selected = useRingtoneId();
  // One live preview player at a time; stopped on unmount and when another is
  // previewed. A short auto-stop keeps a preview from ringing indefinitely.
  const previewRef = useRef<TonePlayer | null>(null);
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [previewing, setPreviewing] = useState<string | null>(null);

  const stopPreview = (): void => {
    if (stopTimer.current) clearTimeout(stopTimer.current);
    stopTimer.current = null;
    previewRef.current?.stop();
    previewRef.current = null;
    setPreviewing(null);
  };
  useEffect(() => stopPreview, []);

  const choose = (id: string): void => {
    setRingtoneId(id);
    // Preview the pick (a tap is the gesture that unlocks the AudioContext).
    stopPreview();
    const player = createRingtoneFor(id);
    previewRef.current = player;
    setPreviewing(id);
    player.start();
    stopTimer.current = setTimeout(stopPreview, 3_500);
  };

  return (
    <Section title={t('settings.ringtone')}>
      <p className="text-xs text-muted">{t('settings.ringtoneHelp')}</p>
      <ul className="flex flex-col gap-1" aria-label={t('settings.ringtone')}>
        {RINGTONES.map((r) => {
          const isSelected = r.id === selected;
          return (
            <li key={r.id}>
              <button
                type="button"
                aria-pressed={isSelected}
                onClick={() => choose(r.id)}
                className={`flex min-h-touch w-full items-center gap-3 rounded-xl p-3 text-left ${
                  isSelected ? 'bg-raised text-ink' : 'bg-surface text-ink active:bg-raised'
                }`}
              >
                <span className="flex-1">{t(r.nameKey)}</span>
                {previewing === r.id && (
                  <span aria-hidden="true" className="text-xs text-muted">
                    {t('settings.ringtonePlaying')}
                  </span>
                )}
                {isSelected && (
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5 text-accent"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/** Web-only ringtone catalog; native uses the system picker (RingtoneSection). */
export function WebRingtoneSection(): ReactNode {
  if (isCapacitorNative()) return null;
  return <RingtonePicker />;
}
